import React, { useEffect, useState } from 'react';
import { doc, collection, onSnapshot } from 'firebase/firestore';
import { Box, Paper, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { db } from '../../firebase';
import { mapProductDoc } from '../../databaseManager';

// Lightweight, read-only version of the ProductListV3 card, used by the uploader to
// show a just-created product while it's still processing. Loading indicators stand
// in for fields (image/title/price/category) that aren't ready yet, instead of
// leaking a preliminary/incomplete guess or dropping straight into the full editor.
export default function UploaderProductPreview({ customerId, productId, onEdit }) {
  const [product, setProduct] = useState(null);

  useEffect(() => {
    const custColRef = collection(db, 'customers');
    const custDocRef = doc(custColRef, customerId);
    const prodColRef = collection(custDocRef, 'products');
    const prodDocRef = doc(prodColRef, productId);

    const unsub = onSnapshot(prodDocRef, (snap) => {
      setProduct(mapProductDoc(snap, { customer: customerId }));
    });
    return () => unsub();
  }, [customerId, productId]);

  if (!product) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 88 }}>
        <CircularProgress size={20} />
      </Paper>
    );
  }

  const imageUrl = product.mainImage || (product.thumbnailUrls || [])[0] || null;
  const titleValue = product.specs?.Title || null;
  const priceValue = product.specs?.Price ? `$${String(product.specs.Price).replace('$', '')}` : null;
  const category = product.specs?.Category;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 1.25 }}>
        {imageUrl ? (
          <Box component="img" src={imageUrl} alt="" sx={{ width: 56, height: 56, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <Box sx={{ width: 56, height: 56, borderRadius: 1.5, bgcolor: 'grey.100', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={16} />
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {titleValue ? (
            <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.92rem' }}>{titleValue}</Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CircularProgress size={12} />
              <Typography variant="body2" color="text.disabled">Generating title…</Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            {priceValue ? (
              <Typography variant="body2" color="text.secondary">{priceValue}</Typography>
            ) : !product.productReady && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={10} />
                <Typography variant="caption" color="text.disabled">Pricing…</Typography>
              </Box>
            )}
            {category && (
              <Typography variant="caption" color="text.disabled" noWrap>{category}</Typography>
            )}
          </Box>
        </Box>

        <Tooltip title="Edit product">
          <IconButton size="small" onClick={onEdit}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
}
